import { Fragment, useMemo } from 'react';
import { InputRuleApiModel, Country } from '~/lib/api/smartsend/types';
import { CustomerSelectionCountry } from './CustomerSelectionCountry';
import { P2GDropdown, P2GGrid, P2GTextField } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';

interface props {
  onUpdate?: (validation: InputRuleApiModel[]) => void;
  validation: InputRuleApiModel[];
  countries: Country[];
}

const updateValue = (validation: InputRuleApiModel[], key: string, value: string) => {
  validation.forEach((n) => {
    if (n.name === key) {
      n.value = value;
    }
  });
};
const toJsValue = (v: string) => (v == null || v == '' || v == ' ' || v == 'null' || v == undefined ? null : v);
const getValue = (validation: InputRuleApiModel[], key: string) => validation.find((v) => v.name === key)?.value;

export const CustomerSelectionValidationInput: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const country = useMemo(() => {
    const countryIsoCode = props.validation.find((x) => x.name === 'CountryIsoCode')?.value;
    const countrySubdivision = props.validation.find((x) => x.name === 'CountrySubdivision')?.value;
    return props.countries.find(
      (x) => x.CountryIsoCode === countryIsoCode && x.CountrySubdivision === toJsValue(countrySubdivision)
    );
  }, [props.countries, props.validation]);

  const onValueUpdate = (field: string, v: string) => {
    const nv = [...props.validation];
    updateValue(nv, field, v);

    // Check this postcode isn't for a different country....
    if (field === 'Postcode') {
      const possibleSwitch = country?.Switches?.find((s) => new RegExp(s.Pattern).test(v?.toUpperCase()));
      const switchCountry =
        possibleSwitch &&
        props.countries.find(
          (c) => c.CountryIsoCode == possibleSwitch.ToIsoCode && c.CountrySubdivision == possibleSwitch.ToSubdivision
        );
      if (switchCountry) {
        updateValue(nv, 'CountryIsoCode', switchCountry.CountryIsoCode);
        updateValue(nv, 'CountrySubdivision', switchCountry.CountrySubdivision);
        if (switchCountry.States?.length > 0 && !switchCountry.States.find((s) => s.Code === getValue(nv, 'County'))) {
          updateValue(nv, 'County', switchCountry.States[0].Code);
        }
      }
    }

    props.onUpdate(nv);
  };

  const renderField = (r: InputRuleApiModel) => {
    const label = t(sanitiseKey(`CUSTOMER_${r.name}_LABEL`), { defaultValue: r.label });

    switch (r.name) {
      case 'CountryIsoCode':
      case 'CountrySubdivision':
        return null;
      case 'County':
        return (
          <P2GGrid item xs={6} key={r.name}>
            {country?.States?.length > 0 ? (
              <P2GDropdown
                fullWidth
                {...r}
                required={false}
                options={
                  country?.States.map((x) => ({ value: x.Code, text: x.Name, key: `${x.Code}_${x.Name}` })) || []
                }
                label={label}
                key={r.name}
                id={r.name}
                onChange={(v: string) => onValueUpdate(r.name, v)}
              />
            ) : (
              <P2GTextField
                fullWidth
                {...r}
                key={r.name}
                showErrorsWhenPristine={true}
                id={r.name}
                onChange={(v) => onValueUpdate(r.name, v.currentTarget.value)}
                helperText={r.helperText === r.label ? '' : r.helperText}
                label={label}
              />
            )}
          </P2GGrid>
        );
      default:
        return (
          <P2GGrid item xs={['Name', 'Email', 'Phone', 'Organisation'].includes(r.name) ? 12 : 6} key={r.name}>
            <P2GTextField
              fullWidth
              {...r}
              key={r.name}
              showErrorsWhenPristine={true}
              id={r.name}
              onChange={(v) => onValueUpdate(r.name, v.currentTarget.value)}
              helperText={r.helperText === r.label ? '' : r.helperText}
              label={label}
            />
          </P2GGrid>
        );
    }
  };

  return (
    <P2GGrid container spacing={2}>
      <P2GGrid item xs={12}>
        <CustomerSelectionCountry country={country} />
      </P2GGrid>
      {props.validation.map((r) => (
        <Fragment key={`r_${r.name}`}>{renderField(r)}</Fragment>
      ))}
    </P2GGrid>
  );
};
